import React, { useEffect, useState, useCallback } from 'react';
import { Input } from 'antd';
import {
  ALL_EXCLUDED,
  COMPETITORS,
  PODIUM_TITLES,
  SEND_TO_CRM,
  AUTHORS,
  PODIUMAUTHORS,
  AUTHOR_TAGS,
  SOURCE,
  SENT_TO_CRM_RR
} from '../../../core/constants';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { IBook } from '../../../section/Common/types/types';
import getTags from '../../../section/Common/utils/getTags';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';
import Endpoints from '../../../core/constants/endpoints';
import BookstatExcludeOptions from './BookstatExcludeOptions';
import Ao3ExcludeTitles from './Ao3ExcludeTitles';

interface IProps {
  book: IBook | any;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const excludeObject = (source, id, book) => {
  if (source === SOURCE.bookstat) {
    return { obj: { amazonasin: book.ASIN, title: book.Title }, obj_type: 'Title' };
  } else if (source === SOURCE['royal-road']) {
    return (id === AUTHORS || id === PODIUMAUTHORS) ?
      { obj: { author_id: book['Author Id'] }, obj_type: 'Author' } :
      { obj: { fiction_id: book['Fiction Id'], title: book.Title }, obj_type: 'Title' };
  }
};

const DefaultExcludeTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const exclude = excludeObject(source['source'], id, book);
  const history = useHistory();

  const { mutateAsync: mutateExclude } = useUpdateTitle(ALL_EXCLUDED);
  const { mutateAsync: mutatePodiumTitle } = useUpdateTitle(PODIUM_TITLES);
  const { mutateAsync: mutateCompetitors } = useUpdateTitle(COMPETITORS);
  const { mutateAsync: mutateExcludeAuthor } = useUpdateTitle(AUTHOR_TAGS);

  const queryClient = useQueryClient();

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [input, setInput] = useState<string>('');
  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [tags, setTags] = useState<CheckboxValueType[]>([]);
  const defaultSelectionMode = 'tags';
  const [selectionMode, setSelectionMode] = useState<'tags' | 'series'>(defaultSelectionMode);
  const [notes, setNotes] = useState<string>('');

  const handleSelectionChange = (e) => {
    const val = e?.target?.value;
    setSelectionMode(val);

    if (val === 'series') {
      setTags([]);
      setIsCheck(false);
      setInput('');
      setButtonDisabled(false);
    }
    else
    {
      setButtonDisabled(true);
    }
  };

  const otherTabs = ({ tab, currentTags, mutateFN, body }) => {
    const index = currentTags.indexOf(tab);

    if (index !== -1) {
      currentTags.splice(index, 1);
      const params = exclude?.obj ?? {};
      const promise = mutateFN({ params, body });
      promise.then(res => {
        if (res?.status === 200) {
          showActionNotification({
            type: 'success',
            message: `The ${exclude?.obj_type} was successfully excluded.`
          });
        } else {
          showActionNotification({
            type: 'error',
            message: `The ${exclude?.obj_type} has not been excluded.`
          });
        }
      }).catch(() => {
        showActionNotification({
          type: 'error',
          message: `The ${exclude?.obj_type} has not been excluded.`
        });
      });
    }
  };

  const handleReset = () => {
    setTags([]);
    setInput('');
    setIsCheck(false);
    setButtonDisabled(true);
    setSelectionMode(defaultSelectionMode);
    setNotes('');
  };

  const onSend = () => {
    setActionLoading(true);
    const currentTags = [...tags];

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    otherTabs({ tab: 'Podium Title', currentTags, mutateFN: mutatePodiumTitle, body });
    otherTabs({ tab: 'Competitors', currentTags, mutateFN: mutateCompetitors, body });

    let tag = '';

    if (selectionMode === 'series') {
      tag = 'Exclude Entire Series';
    } else {
      tag = getTags(currentTags);

      if (isCheck) {
        tag += `&tag=${input}`;
      }
    }

    const params = exclude?.obj ? { ...exclude.obj } : {};
    const finalParams =
    selectionMode === 'series' && source['source'] === SOURCE.bookstat
      ? { ...params, seriesid: book?.seriesid, tag }
      : { ...params, tag };

    const isAuthor = id === AUTHORS || id === PODIUMAUTHORS;
    const capturedParams = isAuthor
      ? { ...params, tag: 'Excluded' }
      : { ...finalParams };

    const capturedSource = source['source'];
    const capturedUndoParams = {
      [SOURCE.bookstat]: { amazonasin: book.ASIN },
      [SOURCE['royal-road']]: { fiction_id: book['Fiction Id'] }
    }[capturedSource];

    const promisePT = isAuthor
      ? mutateExcludeAuthor({ params: capturedParams, body })
      : mutateExclude({ params: capturedParams, body });

    promisePT.then(res => {
      if (res?.status === 200) {
        if (isAuthor) {
          showActionNotification({
            type: 'success',
            message: `The ${exclude?.obj_type} was successfully excluded.`
          });
        } else {
          showActionNotification({
            type: 'success',
            message: `The ${exclude?.obj_type} was successfully excluded.`,
            destination: {
              name: 'All Excluded',
              onGoTo: () => history.push(`${capturedSource}/all-excluded`)
            },
            onUndo: async () => {
              await bookService.updateTitle(
                {
                  source: capturedSource,
                  sourceTab: 'all-excluded',
                  endpoint: Endpoints[id],
                  action: id
                },
                capturedUndoParams
              );
            },
            queryClient,
            id
          });
        }
      } else {
        showActionNotification({
          type: 'error',
          message: `The ${exclude?.obj_type} has not been sent.`
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: `The ${exclude?.obj_type} has not been sent.`
      });
    });

    promisePT.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR) {
        showActionNotification({ type: 'info', message: 'Please delete the data from Hubspot.' });
      }
    });

    promisePT.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promisePT.then(() => setActionLoading(false));
    promisePT.then(() => queryClient.refetchQueries(`get-${id}`));
    toggleModal();
    handleReset();
  };

  const handleCheckboxGroup = useCallback(
    value => {
      setTags(value);

      if (value.length) {
        if (value.includes(undefined) && !input) {
          setButtonDisabled(true);
          return;
        } else {
          setButtonDisabled(false);
          return;
        }
      }
      setButtonDisabled(true);
    },
    [input]
  );

  const handleInput = useCallback(({ target }) => {
    setInput(target.value);
  }, []);

  useEffect(() => {
    if (input && isCheck && tags?.length) {
      setButtonDisabled(false);
    } else if (isCheck && !input) {
      setButtonDisabled(true);
    }
  }, [isCheck, input]);

  const seriesNote = (
    <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.65)' }}>
      <ul style={{ paddingLeft: 18 }}>
        <li>
            When selected, the title will be tagged as Exclude Entire Series.
        </li>
        <li>
            Future titles from this series will be automatically sent to the Exclusion List.
        </li>
        <li>
            Other titles from the author that are not part of this series will not be sent
            to the Exclusion List.
        </li>
      </ul>
    </div>
  );

  const renderExcludeOptions = () => {
    if (source['source'] === SOURCE['royal-road']) {
      return <p>{`Sending ${exclude?.obj_type} to Exclusion List. Please confirm.`}</p>;
    }

    return (
      <BookstatExcludeOptions
        selectionMode={selectionMode}
        onSelectionChange={handleSelectionChange}
        tags={tags}
        onTagsChange={handleCheckboxGroup}
        isCheck={isCheck}
        setIsCheck={setIsCheck}
        input={input}
        onInputChange={handleInput}
        seriesNote={seriesNote}
      />
    );
  };

  return (
    <Modal
      okText={`Exclude ${exclude?.obj_type}`}
      title={`Send ${exclude?.obj_type} to Exclusion List`}
      visible={modal}
      onOk={onSend}
      okButtonProps={{
        disabled: source['source'] === SOURCE['royal-road'] ? false : buttonDisabled
      }}
      onCancel={() => {
        toggleModal();
        handleReset();
      }}
    >
      {renderExcludeOptions()}

      <Input.TextArea
        placeholder='Leave a note stating the reason (optional)'
        maxLength={400}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
      />

      <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
        {notes.length} / 400
      </div>
    </Modal>
  );
};

const ExcludeTitles: React.FC<IProps> = (props) => {
  const { source } = useActions();

  return source['source'] === SOURCE.ao3
    ? <Ao3ExcludeTitles {...props} />
    : <DefaultExcludeTitles {...props} />;
};

export default React.memo(ExcludeTitles);
